
import { MessageCircle, Triangle, Image } from "lucide-react";
import type { Tool } from "@/data/tools";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ToolCardProps {
  tool: Tool;
}

const ToolCard = ({ tool }: ToolCardProps) => {
  const navigate = useNavigate();
  const [sportSlugs, setSportSlugs] = useState<{[key: string]: string}>({});
  
  // Color mappings for sports pills
  const sportColors: { [key: string]: string } = {
    Running: "bg-[#F97316] text-white",
    Cycling: "bg-[#0EA5E9] text-white",
    Swimming: "bg-[#8B5CF6] text-white",
    Rowing: "bg-[#D946EF] text-white",
    Tennis: "bg-[#F97316] text-white",
    Basketball: "bg-[#8B5CF6] text-white",
    Soccer: "bg-[#0EA5E9] text-white",
    Golf: "bg-[#D946EF] text-white",
    Weightlifting: "bg-[#F97316] text-white",
    Marathon: "bg-[#0EA5E9] text-white",
    Yoga: "bg-[#8B5CF6] text-white",
  };

  // Color mappings for platform pills
  const platformColors: { [key: string]: string } = {
    iOS: "bg-[#E5DEFF] text-[#6B4EFF]",
    Android: "bg-[#FDE1D3] text-[#F97316]",
    Web: "bg-[#D3E4FD] text-[#0E7490]",
  };

  // Function to check if image URL is valid and exists
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = "https://placehold.co/60x60"; // Fallback to placeholder if image fails to load
  };

  // Check if logo is valid
  const isValidLogo = tool.logo && 
                     tool.logo !== "https://placehold.co/60x60" && 
                     tool.logo.trim() !== "";

  // Get the URL slug from the tool.id (temporary solution until all tools have a url_slug)
  const urlSlug = tool.url_slug || tool.id;

  // Helper to convert name to URL slug - consistently handle special characters
  const getSlug = (name: string) => {
    // Consistent conversion of special characters for URLs:
    // - Convert & to "and" (not just -&-)
    // - Convert all spaces to dashes
    // - Convert to lowercase
    return name.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/&/g, 'and')
      .replace(/[^\w\-]+/g, ''); // Remove any remaining special chars
  };

  // Fetch actual slugs from the database on component mount
  useEffect(() => {
    const fetchSportSlugs = async () => {
      try {
        const { data, error } = await supabase
          .from('sports')
          .select('name, url_slug')
          .order('name');
        
        if (error) {
          console.error('Error fetching sport slugs:', error);
          return;
        }
        
        if (data) {
          const slugMap: {[key: string]: string} = {};
          data.forEach(sport => {
            slugMap[sport.name] = sport.url_slug;
          });
          setSportSlugs(slugMap);
        }
      } catch (error) {
        console.error('Error in fetchSportSlugs:', error);
      }
    };
    
    fetchSportSlugs();
  }, []);

  // Determine card background based on featured status
  const cardBackground = tool.featured 
    ? "bg-gradient-to-r from-[#FEF7CD] to-[#FFF9E0] hover:bg-[#FEF3B5]" 
    : "hover:bg-gray-50";

  return (
    <Link to={`/tool/${urlSlug}`}>
      <div className={`flex items-start gap-4 p-4 rounded-lg transition-colors ${cardBackground}`}>
        <div className="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden">
          {isValidLogo ? (
            <img 
              src={tool.logo} 
              alt={tool.name} 
              className="w-full h-full object-cover" 
              onError={handleImageError}
            />
          ) : (
            <Image className="w-6 h-6 text-gray-300" />
          )}
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg">
              {tool.name}
            </h3>
            {tool.featured && (
              <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                Featured
              </span>
            )}
          </div>
          
          <p className="text-gray-600 mt-1 line-clamp-1">{tool.description}</p>
          
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {/* Sports pills */}
            {tool.sports.map((sport) => (
              <span 
                key={sport}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Use the actual slug from Supabase or fallback to the converted slug
                  const sportSlug = sportSlugs[sport] || getSlug(sport);
                  // Use React Router's navigate instead of direct location change
                  navigate(`/sport/${sportSlug}`);
                }}
                className={`px-2 py-1 rounded-full text-xs font-medium hover:opacity-80 transition-colors cursor-pointer ${sportColors[sport] || 'bg-gray-100 text-gray-800'}`}
              >
                {sport}
              </span>
            ))}
            
            {/* Platform pills */}
            {tool.platforms.map((platform) => (
              <span
                key={platform}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Use React Router's navigate instead of direct location change
                  navigate(`/platform/${getSlug(platform)}`);
                }}
                className={`px-2 py-1 rounded-full text-xs font-medium hover:opacity-80 transition-colors cursor-pointer ${platformColors[platform] || 'bg-gray-100 text-gray-800'}`}
              >
                {platform}
              </span>
            ))}
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <MessageCircle className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">{tool.reviews}</span>
          </div>
          <div className="flex items-center gap-1">
            <Triangle className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">{tool.bookmarks}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ToolCard;
